import React, { useMemo, useState } from 'react';
import { Checkbox, Col, DatePicker, Divider, Form, Input, Radio, Row, Space, Table, Typography, ConfigProvider } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import type { FormInstance } from 'antd';
import FileDownloadTool from '../../../../../../components/common/FileDownloadTool';
import FileUploadTool from '../../../../../../components/common/FileUploadTool';
import PipeHeatNoSelect from '../../../../../../components/modals/PipeHeatNoSelect';
import WelderSelect from '../../../../../../components/modals/WelderSelect';
import PersonnelSelect from '../../../../../../components/modals/PersonnelSelect';
import { useInspectionResultsSync } from './inspection-form/useInspectionResultsSync';
import WeldMetaBlock from './inspection-form/WeldMetaBlock';
import PtPanel from './inspection-form/panels/PtPanel';
import MtPanel from './inspection-form/panels/MtPanel';
import UtPanel from './inspection-form/panels/UtPanel';
import BcPanel from './inspection-form/panels/BcPanel';
import VcPanel from './inspection-form/panels/VcPanel';
import Button from '../../../../../../components/common/Button';

const { Text } = Typography;
const { TextArea } = Input;

export type PipeInspectionMode = 'create' | 'edit' | 'view';

interface Props {
  form: FormInstance;
  mode: PipeInspectionMode;
  inspectionType: string | undefined;
  weldingdataIds: Array<string | number>;
  uploadPath?: string;
  fileUploadRef?: React.Ref<any>;
  weldRevMap?: Record<string, number | null>;
  weldMetaMap?: Record<string, {
    pipe_size?: string | null,
    service_class_name?: string | null,
    weldtype_name?: string | null,
    spool_id?: number | null,
    weld_category?: string | null,
    code_no?: string | null,
    fluid?: string | null,
  }>;
}

export default function PipeInspectionForm(props: Props) {
  const { form, mode, inspectionType, weldingdataIds, uploadPath, fileUploadRef, weldRevMap, weldMetaMap } = props;
  const [expandedKeys, setExpandedKeys] = useState<string[]>(() => (weldingdataIds || []).map((w) => String(w)));
  const [attModalOpen, setAttModalOpen] = useState(false);
  const [extraAttendees, setExtraAttendees] = useState<string[]>(() => Array.from(new Set((form.getFieldValue('attendance') || []) as string[])));
  const [heatModalOpen, setHeatModalOpen] = useState(false);
  const [heatModalTarget, setHeatModalTarget] = useState<{ weldKey?: string; field?: 'heat_no_1' | 'heat_no_2' }>({});
  const [welderModalOpen, setWelderModalOpen] = useState(false);
  const [welderTarget, setWelderTarget] = useState<{ weldKey?: string; layer?: 'flw' | 'fw' }>({});
  const watchedAttendance = (Form as any).useWatch ? (Form as any).useWatch('attendance', form) : undefined;
  React.useEffect(() => {
    const arr: string[] = Array.isArray(watchedAttendance) ? watchedAttendance : (Array.isArray(form.getFieldValue('attendance')) ? form.getFieldValue('attendance') : []);
    setExtraAttendees(Array.from(new Set(arr)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedAttendance]);

  useInspectionResultsSync(form, inspectionType, weldingdataIds);

  const isView = mode === 'view';
  const viewTheme = React.useMemo(() => (
    isView
      ? {
          token: {
            colorTextDisabled: 'rgba(0,0,0,0.88)',
            colorBgContainerDisabled: 'transparent',
            colorBorder: '#d9d9d9',
          },
        }
      : undefined
  ), [isView]);

  const dataSource = useMemo(
    () => (weldingdataIds || []).map((w) => ({ key: String(w), weld: String(w) })),
    [weldingdataIds]
  );

  return (
    <ConfigProvider theme={viewTheme as any}>
      <>
      {/* Header fields (hidden in create; shown in edit/view) */}
      {mode !== 'create' && (
        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item label={<b>Report Number</b>} name="report_id" style={{ marginBottom: 8 }}>
              <Input readOnly />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label={<b>Issued By</b>} name="issued_by" style={{ marginBottom: 8 }}>
              <Input disabled />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label={<b>Report Date</b>} name="report_date" style={{ marginBottom: 8 }}>
              <DatePicker style={{ width: '100%' }} disabled={mode === 'view'} />
            </Form.Item>
          </Col>
        </Row>
      )}
      <Row gutter={12}>
        <Col xs={12} md={6}>
          <Form.Item label={<b>RFI No</b>} name="rfi_no" style={{ marginBottom: 8 }}>
            <Input readOnly />
          </Form.Item>
        </Col>
        <Col xs={12} md={6}>
          <Form.Item label={<b>WP Ref</b>} name="work_permit_ref_num" style={{ marginBottom: 8 }}>
            <Input readOnly />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label={<b>WP Description</b>} name="work_permit_description" style={{ marginBottom: 8 }}>
            <Input readOnly />
          </Form.Item>
        </Col>
      </Row>
      <PipeHeatNoSelect
        open={!!heatModalOpen}
        onCancel={() => setHeatModalOpen(false)}
        onOk={(heatNo) => {
          const k = heatModalTarget.weldKey;
          const f = heatModalTarget.field;
          if (k && f) {
            // Form store tek doğruluk kaynağı; Form.Item bağlı Input kendini günceller
            form.setFields([{ name: ['results', k, 'bc', f], value: heatNo }]);
          }
          setHeatModalOpen(false);
        }}
      />
      <WelderSelect
        open={!!welderModalOpen}
        maxCount={5}
        initialSelected={(welderTarget.weldKey && welderTarget.layer)
          ? (() : string[] => {
              const key = String(welderTarget.weldKey);
              const layer = welderTarget.layer as 'flw' | 'fw';
              const s = form.getFieldValue(['results', key, 'vc', `${layer}_welder`]);
              const t = (s != null ? String(s) : '').trim();
              return t ? t.split(',').map((x: string) => x.trim()).filter(Boolean) : [];
            })()
          : []}
        onCancel={() => setWelderModalOpen(false)}
        onOk={(codes) => {
          const k = welderTarget.weldKey;
          const layer = welderTarget.layer;
          if (k && layer) {
            const safe = Array.isArray(codes) ? codes.filter(Boolean).slice(0,5).map(String) : [];
            const selectedStr = safe.join(', ');
            // Form store tek doğruluk kaynağı; details_payloads senkronu useInspectionResultsSync'te
            const setArr: any[] = [{ name: ['results', k, 'vc', `${layer}_welder`], value: selectedStr }];
            // If selecting FLW and FW is empty, default FW to same selection
            if (layer === 'flw') {
              const fwPrev = (form.getFieldValue(['results', k, 'vc', 'fw_welder']) ?? '').toString().trim();
              if (!fwPrev) setArr.push({ name: ['results', k, 'vc', 'fw_welder'], value: selectedStr });
            }
            form.setFields(setArr);
          }
          setWelderModalOpen(false);
        }}
      />
      {/* Attendance with modal */}
      <Row gutter={[16, 8]}>
        <Col span={24}>
          <Form.Item
            label={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span><b>Attendance</b></span>
                {mode !== 'view' && (
                  <Button size="small" icon={<CheckOutlined />} onClick={() => setAttModalOpen(true)}>Add</Button>
                )}
              </div>
            }
            name="attendance"
          >
            <Checkbox.Group style={{ width: '100%' }}>
              <Space wrap>
                {Array.from(new Set(extraAttendees)).map((name) => (
                  <span key={name} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Checkbox value={name} disabled={mode === 'view'}>{name}</Checkbox>
                    {mode !== 'view' && extraAttendees.includes(name) && (
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<CloseOutlined />}
                        onClick={() => {
                          setExtraAttendees(prev => prev.filter(n => n !== name));
                          const cur: string[] = form.getFieldValue('attendance') || [];
                          if (Array.isArray(cur) && cur.includes(name)) {
                            form.setFieldsValue({ attendance: cur.filter(n => n !== name) });
                          }
                        }}
                      />
                    )}
                  </span>
                ))}
              </Space>
            </Checkbox.Group>
          </Form.Item>
        </Col>
      </Row>

      {Array.isArray(weldingdataIds) && weldingdataIds.length > 0 && (
        <>
          <Divider style={{ margin: '0px 0' }} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography.Title level={5} style={{ margin: 0 }}>Results</Typography.Title>
            {(inspectionType === 'BC' || inspectionType === 'VC' || inspectionType === 'PT' || inspectionType === 'MT' || inspectionType === 'UT') && (
              <Space size={6}>
                <Button size="small" onClick={() => setExpandedKeys((weldingdataIds || []).map((w) => String(w)))}>Expand all</Button>
                <Button size="small" onClick={() => setExpandedKeys([])}>Collapse all</Button>
              </Space>
            )}
          </div>
          <Table
            size="small"
            pagination={false}
            dataSource={dataSource}
            rowKey="key"
            columns={[
              {
                title: 'Weld',
                dataIndex: 'weld',
                key: 'weld',
                width: 220,
                render: (v: string, row: any) => {
                  const rev = weldRevMap?.[row.key as string];
                  return (
                    <span>
                      <Text strong>{v}</Text>
                      {rev != null && <Text type="secondary"> {` (Rev ${rev})`}</Text>}
                    </span>
                  );
                },
              },
              {
                title: 'Result',
                key: 'result',
                width: 110,
                render: (_: any, r: any) => (
                  <Form.Item shouldUpdate={(prev, cur) => prev?.results?.[r.key]?.result !== cur?.results?.[r.key]?.result} noStyle>
                    {() => {
                      const sel = form.getFieldValue(['results', r.key, 'result']);
                      const isPass = sel === 'PASS';
                      const isFail = sel === 'FAIL';
                      const isRework = sel === 'REWORK';
                      const baseStyle: React.CSSProperties = { minWidth: 28, padding: '0 4px', textAlign: 'center', lineHeight: '22px', height: 22, fontSize: 12 };
                      const passStyle: React.CSSProperties = isPass ? { background: '#1677ff', color: '#fff', borderColor: '#1677ff' } : {};
                      const failStyle: React.CSSProperties = isFail ? { background: '#ff4d4f', color: '#fff', borderColor: '#ff4d4f' } : {};
                      const reworkStyle: React.CSSProperties = isRework ? { background: '#ffd27f', color: '#000', borderColor: '#ffd27f' } : {};
                      return (
                        <Form.Item name={['results', r.key, 'result']} style={{ margin: 0 }} initialValue={'PASS'}>
                          <Radio.Group style={{ whiteSpace: 'nowrap' }} disabled={isView}>
                            <Radio.Button value="PASS" style={{ ...baseStyle, ...passStyle }}>P</Radio.Button>
                            <Radio.Button value="FAIL" style={{ ...baseStyle, ...failStyle }}>F</Radio.Button>
                            <Radio.Button value="REWORK" style={{ ...baseStyle, ...reworkStyle }}>R</Radio.Button>
                          </Radio.Group>
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                )
              },
              {
                title: 'Hardness',
                key: 'hardness',
                width: 100,
                render: (_: any, r: any) => (
                  <Form.Item name={['results', r.key, 'hardness']} style={{ margin: 0 }}>
                    <Input placeholder={inspectionType === 'PWHT' ? 'Hardness (HB/HV)' : 'N/A'} disabled={inspectionType !== 'PWHT' || isView} />
                  </Form.Item>
                )
              },
            ] as any}
            expandable={{
              expandedRowRender: (r: any) => (
                inspectionType === 'BC' ? (
                  <BcPanel
                    form={form}
                    weldKey={r.key}
                    isView={isView}
                    meta={weldMetaMap?.[r.key]}
                    onOpenHeatSelect={(field) => { setHeatModalTarget({ weldKey: r.key, field }); setHeatModalOpen(true); }}
                  />
                ) : inspectionType === 'VC' ? (
                  <VcPanel
                    form={form}
                    weldKey={r.key}
                    isView={isView}
                    meta={weldMetaMap?.[r.key]}
                    onOpenWelderSelect={(layer) => { setWelderTarget({ weldKey: r.key, layer }); setWelderModalOpen(true); }}
                  />
                ) : inspectionType === 'PT' ? (
                  <PtPanel form={form} weldKey={r.key} isView={isView} meta={weldMetaMap?.[r.key]} />
                ) : inspectionType === 'MT' ? (
                  <MtPanel form={form} weldKey={r.key} isView={isView} meta={weldMetaMap?.[r.key]} />
                ) : inspectionType === 'UT' ? (
                  <UtPanel form={form} weldKey={r.key} isView={isView} meta={weldMetaMap?.[r.key]} />
                ) : null
              ),
              expandedRowKeys: expandedKeys,
              onExpandedRowsChange: (keys: any) => setExpandedKeys(keys as string[]),
              expandRowByClick: false,
            }}
            scroll={{ x: true }}
            bordered
          />
        </>
      )}

      <Form.Item label={<b>Notes</b>} name="report_notes" style={{ marginTop: 12, marginBottom: 8 }}>
        <TextArea rows={3} disabled={isView} />
      </Form.Item>

      <Space style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        {mode !== 'view' && <FileUploadTool ref={fileUploadRef} buttonText="Upload Files" />}
        {uploadPath && uploadPath.trim() !== '' && (
          <FileDownloadTool
            buttonText={(<Button style={{ backgroundColor: '#52c41a', color: '#fff', borderColor: 'transparent' }}>Attached Files</Button>)}
            contentPath={uploadPath as string}
          />
        )}
      </Space>

      <Form.Item name="results_codes" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="pwht_hardness_list" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="pwht_hardness_out_of_range" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="pwht_hardness_invalid_welds" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="has_fail_or_rework" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="fail_count" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="rework_count" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="combined_remarks" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="require_fail_rework_explanation" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="final_notes" hidden>
        <Input />
      </Form.Item>
      {/* Ensure normalized per-weld payloads are registered for onFinish */}
      <Form.Item name="details_payloads" hidden />

      <PersonnelSelect
        open={attModalOpen}
        onCancel={() => setAttModalOpen(false)}
        onOk={(val: any) => {
          const arr = Array.isArray(val) ? val : [val];
          const names: string[] = [];
          const pids: string[] = [];
          arr.forEach((s: any) => {
            let pid: string | undefined;
            let displayName: string | undefined;
            if (Array.isArray(s)) {
              pid = s[0] != null ? String(s[0]).trim() : undefined;
              displayName = s[s.length - 1] != null ? String(s[s.length - 1]) : undefined;
            } else {
              const str = String(s);
              const idx = str.indexOf(' - ');
              if (idx >= 0) {
                pid = str.slice(0, idx).trim();
                displayName = str.slice(idx + 3).trim();
              } else {
                displayName = str.trim();
              }
            }
            const companyCode = pid ? String(pid).slice(0, 3) : '';
            const formatted = companyCode ? `${displayName} (${companyCode})` : (displayName || '');
            if (formatted) names.push(formatted);
            if (pid) pids.push(pid);
          });
          // Merge into available tags
          setExtraAttendees(prev => Array.from(new Set([...(prev || []), ...names])));
          // Merge into selected attendance and attendee_ids form values
          const prevSel: string[] = Array.isArray(form.getFieldValue('attendance')) ? form.getFieldValue('attendance') : [];
          const nextSel = Array.from(new Set([...(prevSel || []), ...names]));
          const prevIds: string[] = Array.isArray(form.getFieldValue('attendee_ids')) ? form.getFieldValue('attendee_ids') : [];
          const nextIds = Array.from(new Set([...(prevIds || []), ...pids]));
          form.setFieldsValue({ attendance: nextSel, attendee_ids: nextIds });
          setAttModalOpen(false);
        }}
        multi
        mode="both"
      />
      </>
    </ConfigProvider>
  );
}
